// Bailey's plans: what's worth doing this week, as finished campaigns.
//
// Pure: everything it knows comes in (the week's free time, reviews, team,
// UK dates, audience sizes, what's already planned, what the salon said no
// to), so it's easy to test and the same inputs could go to a Bailey
// endpoint later. Each plan is a goal, a reason, one audience and dated items
// across text, email, post and Story, every one written and designed. Nothing
// is invented: times come from the diary, reviews word for word, dates from
// the UK calendar.
import moment from 'moment'
import { realPost, showcaseFor } from '../../social/data/postTypes'
import { hasSeasonal, seasonalFor, seasonalLayout, withSalon } from '../../social/kits'
import { campaignMoments, momentTemplates, upcomingMoments } from '../../social/data/seasonal'
import { smsText } from './sms'
import { templateFor } from './emailFor'
import { seasonCampaign, designPicker, photosFromPosts } from '../../campaigns/data/story'

const first = (s) => String(s || '').trim().split(/\s+/)[0] || ''
const at = (day, h, m = 0) => moment(day).hours(h).minutes(m).seconds(0).milliseconds(0)
// The day as said in a sentence: "tomorrow", "on Thursday", "on Fri 3 Oct"
const said = (date, now) => {
  const d = moment(date).startOf('day')
  const n = moment(now).startOf('day')
  const diff = d.diff(n, 'days')
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff < 7) return `on ${d.format('dddd')}`
  return `on ${d.format('ddd D MMM')}`
}
const Said = (date, now) => { const s = said(date, now).replace(/^on /, ''); return s.charAt(0).toUpperCase() + s.slice(1) }

const iso = (d) => moment(d).format('YYYY-MM-DD')
// The first and last day a plan runs, from what's in it
const windowOf = (items) => {
  const ats = items.map(i => moment(i.at)).sort((a, b) => a - b)
  return ats.length ? { start: iso(ats[0]), end: iso(ats[ats.length - 1]) } : { start: null, end: null }
}
const withWindow = (plan) => (plan ? { ...windowOf(plan.items), ...plan } : plan)

// Texts: one message where they can, with the opt-out (sms.js)
export { smsText } from './sms'

// The audience for a plan, from the salon's segments (ids the campaign API uses)
const pickSegment = (segments, ids, fallbackLabel) => {
  const list = Array.isArray(segments) ? segments : []
  for (let i = 0; i < ids.length; i += 1) {
    const s = list.find(x => x && x.id === ids[i])
    if (s) return { segment: s.id, label: s.name || fallbackLabel, count: Number(s.count) || null }
  }
  return { segment: ids[0], label: fallbackLabel, count: null }
}

// Who to text about free times. QA (2026-09-30): "Fill Friday's gaps" texted
// all 352 clients (about £21 of credits) for three slots. Overdue regulars,
// the clients already due a visit, are the ones a spare slot suits, and
// enough of them fill a few; everyone only when there aren't enough of them.
const FILL_MIN = 10
export const fillAudience = (segments, slots = 3) => {
  const overdue = (Array.isArray(segments) ? segments : []).find(x => x && x.id === 'overdue')
  const n = overdue ? Number(overdue.count) || 0 : 0
  if (n >= Math.max(FILL_MIN, slots * 4)) return { segment: 'overdue', label: overdue.name || 'Overdue regulars', count: n }
  return pickSegment(segments, ['all'], 'All your clients')
}

// Posts in a plan wear the salon's brand, so the email, text and posts look
// like one campaign (or, with no brand colour, the job's own design). A
// `design` from designPicker gives each post in a plan a different one.
// Only real things: a design's example facts (a price, a time, a role, a
// voucher amount) and stock photos never go out as the salon's (realPost).
// Someone with no photo gets the name-only design rather than a stranger's.
const postFor = (type, { trade, specialism, family, brandColour, content, photos, source, format, design }) => {
  const noPhoto = type === 'team' && !(photos && photos.main && photos.main.src)
  const show = showcaseFor(type)
  const d = noPhoto ? { family: 'layouts', layout: 'monogram' } : (design || (brandColour ? { family } : show))
  const own = d.family === show.family && (d.layout || null) === (show.layout || null)
  return {
    ...realPost(type, { trade, specialism, family: d.family, layout: d.layout, colourway: brandColour ? 'brand' : own ? show.colourway : undefined, brandColour, content, photos, source, format }),
    status: 'draft',
  }
}

// The lowest voucher on sale online, as a post says it, or '' when none is
const voucherFrom = (vouchers, currency = '£') => {
  const prices = (vouchers || []).map(v => Number(v.fromPrice)).filter(n => n > 0)
  if (!prices.length) return ''
  const low = Math.min(...prices)
  return `From ${currency}${low % 1 ? low.toFixed(2) : low}`
}

// A date's Story from the salon's real things. Its own kind of post when the
// salon has what that needs (vouchers on sale online, a photo of its work);
// otherwise an announcement, in the season's design when there is one. Never
// a design's example free times, offer price or voucher amount.
// The kind of post a date's Story really is, from what the salon has
export const momentKind = (m, ctx) => {
  if (m.type === 'giftVouchers' && voucherFrom(ctx.vouchers, ctx.currency)) return 'giftVouchers'
  if (m.type === 'freshWork' && (ctx.photos || [])[0]) return 'freshWork'
  if (m.type === 'tips') return 'tips'
  return 'announce'
}
const momentPost = (m, ctx, extra) => {
  const c = m.content || {}
  const base = { ...ctx, ...extra, format: 'story', source: `moment:${m.key}:story` }
  const kind = momentKind(m, ctx)
  if (kind === 'giftVouchers') return postFor('giftVouchers', { ...base, content: { ...c, from: voucherFrom(ctx.vouchers, ctx.currency) } })
  if (kind === 'freshWork') return postFor('freshWork', { ...base, content: c, photos: { main: { src: ctx.photos[0], fx: 0.5, fy: 0.4, zoom: 1 } } })
  if (kind === 'tips') return postFor('tips', { ...base, content: c })
  const cta = c.cta || 'Book online, link in bio'
  const layout = hasSeasonal(m.id) ? seasonalFor(m.id, 'open') : null
  if (layout) {
    const words = withSalon(seasonalLayout(layout).content(), ctx.salonName)
    return postFor('announce', { ...base, design: { family: 'layouts', layout }, content: { ...words, cta } })
  }
  return postFor('announce', { ...base, content: { eyebrow: c.eyebrow || m.name, headline: c.headline || m.name, body: '', cta } })
}

// How a fill plan works, said as it will happen (the text goes the evening
// before, or that morning when the day is today)
const fillHow = (D, now, noClients, textAt) => {
  const today = moment(D).isSame(now, 'day')
  if (noClients) return today ? 'A Story this morning puts the times in front of your followers.' : 'A Story that morning puts the times in front of your followers.'
  if (today) return 'A text and a Story this morning usually fill a few.'
  // Too late for the evening before (it's past 6pm): the text goes that morning
  const morning = textAt && moment(textAt).isSame(D, 'day')
  return morning ? 'A text and a Story that morning usually fill a few.' : 'A text the evening before and a Story that morning usually fill a few.'
}

// ─── The recipes ───
const fillPlan = (ctx) => {
  const { now, week, salonName, segments } = ctx
  if (!Array.isArray(week) || !week.length) return null
  // The quietest day soon (sooner wins a tie): today only if it's early
  const today = moment(now).startOf('day')
  const days = week
    .map(d => ({ ...d, idx: moment(d.date).diff(today, 'days') }))
    .filter(d => d.idx >= 0 && d.idx <= 6 && (d.count >= 3 || d.freeHours >= 3))
    .filter(d => d.idx > 0 || moment(now).hours() < 10)
    .sort((a, b) => (b.freeHours - b.idx * 0.75) - (a.freeHours - a.idx * 0.75))
  const day = days[0]
  if (!day) return null
  const D = moment(day.date)
  const times = (day.times || []).slice(0, 3)
  // Text the evening before, or first thing on the day itself
  let textAt = at(moment(D).subtract(1, 'day'), 18)
  if (!textAt.isAfter(now)) textAt = at(D, 9, 30)
  if (!textAt.isAfter(now)) textAt = moment(now).add(15, 'minutes').startOf('minute')
  let storyAt = at(D, 8)
  if (!storyAt.isAfter(now)) storyAt = moment(now).add(20, 'minutes').startOf('minute')
  const we = ctx.self ? 'I’ve' : 'we’ve'
  let text = smsText([`Hi {{FirstName}}, ${we} a few spaces at {{SalonName}} ${said(D, textAt)} (${times.join(', ')}).`, 'Book yours: {{BookingLink}}'], salonName)
  if (text.segments > 1) text = smsText([`Hi {{FirstName}}, ${we} a few spaces at {{SalonName}} ${said(D, textAt)}.`, 'Book yours: {{BookingLink}}'], salonName)
  const audience = fillAudience(segments, Math.max(times.length, Math.min(day.count, 6)))
  // A self-employed stylist's week is theirs: their name goes on the posts
  const whose = day.who ? { staffId: day.who.id, staffName: day.who.firstName } : {}
  const content = { date: day.date, times, more: day.count > times.length, ...whose }
  // The text goes out only if one of these times is still free when it's due
  // (the send re-reads the diary; see approve.js)
  // A day that's one long gap (a new diary) is wide open, not "1 free
  // slot", and its Story shows the free times rather than a last slot
  const open = day.count <= 2 && day.freeHours >= 6
  const one = day.count === 1 && !open
  const guard = times.length ? { type: 'slots', date: day.date, times, ...(Array.isArray(day.staffIds) && day.staffIds.length ? { staffIds: day.staffIds } : {}) } : null
  return {
    key: `fill:${day.date}`,
    kind: 'fill',
    title: `Fill ${said(D, now) === 'today' ? 'today’s' : said(D, now) === 'tomorrow' ? 'tomorrow’s' : `${D.format('dddd')}’s`} gaps`,
    why: `${open ? `${Said(D, now)} is wide open, about ${Math.round(day.freeHours)} hours of chair time` : `${Said(D, now)} still has ${day.count} free ${day.count === 1 ? 'slot' : 'slots'}${day.freeHours <= 16 ? `, about ${Math.max(1, Math.round(day.freeHours))} hours of chair time` : ''}`}. ${fillHow(D, now, ctx.noClients, textAt)}`,
    audience,
    items: [
      !ctx.noClients && { id: 'sms', channel: 'sms', at: textAt.toISOString(), on: true, title: `Spaces ${said(D, textAt)}`, message: text.message, ...(guard ? { guard } : {}) },
      { id: 'story', channel: 'story', at: storyAt.toISOString(), on: true, title: 'Free slots Story', post: postFor(one ? 'oneSlot' : 'freeSlots', { ...ctx, format: 'story', source: `slots:${day.date}`, content: one ? { date: day.date, time: times[0], ...whose } : content }) },
      { id: 'post', channel: 'post', at: at(moment(D).subtract(1, 'day'), 12, 30).isAfter(now) ? at(moment(D).subtract(1, 'day'), 12, 30).toISOString() : storyAt.toISOString(), on: false, title: 'Free slots post', post: postFor('freeSlots', { ...ctx, format: 'post', source: `slots:${day.date}`, content }) },
    ].filter(Boolean),
    score: 100 - day.idx * 5 + day.freeHours,
  }
}

// A season or a date as a campaign across its whole window: a story told in
// posts, Stories, emails and a text, from the salon's own things
// (campaigns/data/story.js)
export const seasonPlan = (m, ctx) => seasonCampaign(m, ctx)

// Which dates to run, two at most. Each date is in a lane: selling vouchers
// (a gift date while the salon sells them online), a season (autumn,
// Movember) or a date to book for (everything else). Never two campaigns in
// the same lane at once, so clients don't get two voucher emails or two
// "book for December" texts in the same week. Selling vouchers comes first, and the longer gift season
// wins over a date inside it (Christmas vouchers runs over Black Friday).
// A booking date comes soonest to finish first, so a date close at hand isn't
// hidden behind a long season that's already running. Without vouchers on
// sale a date that's only about buying (Black Friday) is left out, and the
// last days of a gift date are only a last-minute voucher push.
export const pickMoments = (ctx) => {
  const { now, trade, country, done } = ctx
  const sells = Array.isArray(ctx.vouchers) ? ctx.vouchers.length > 0 : null // null: not known
  const lane = m => (m.kind === 'gift' ? (sells !== false ? 'gift' : 'book') : m.kind === 'season' ? 'season' : 'book')
  const all = campaignMoments(trade, now, country, { late: true })
    .filter(m => !done(`moment:${m.key}`))
    .filter(m => !(m.giftOnly && sells === false))
    .filter(m => !m.late || (m.kind === 'gift' && sells === true))
  const gifts = all.filter(m => lane(m) === 'gift').sort((a, b) => (b.length || 0) - (a.length || 0) || a.end - b.end)
  const books = all.filter(m => lane(m) !== 'gift').sort((a, b) => a.end - b.end)
  const out = []
  const overlaps = (a, b) => a.start <= b.end && b.start <= a.end
  gifts.concat(books).forEach((m) => {
    if (out.length >= 2) return
    if (out.some(x => lane(x) === lane(m) && overlaps(x, m))) return
    out.push(m)
  })
  return out
}
const momentAt = n => (ctx) => {
  const m = pickMoments(ctx)[n]
  if (!m) return null
  const plan = seasonPlan(m, ctx)
  if (!plan) return null
  // Selling vouchers in the gift season is worth the most; a booking
  // campaign is worth less when the diary is already full
  const lift = plan.occasion === 'gift' ? 6 : ctx.full ? -30 : 0
  return { ...plan, score: plan.score + lift }
}
const momentPlan = momentAt(0)
const nextMomentPlan = momentAt(1)

// A gift date coming and no vouchers on sale online: the most useful thing
// is to start selling them, so the campaign can. Owners only (vouchers are
// the salon's).
const vouchersPlan = (ctx) => {
  const { now, trade, country, done, self } = ctx
  if (self || !Array.isArray(ctx.vouchers) || ctx.vouchers.length) return null
  if (done('vouchers')) return null
  const m = campaignMoments(trade, now, country).find(x => x.kind === 'gift')
  if (!m) return null
  const when = said(m.eventDate || m.date, now)
  return {
    key: `vouchers:${m.key}`,
    kind: 'vouchers',
    title: 'Sell gift vouchers online',
    why: `${m.event || m.name} is ${when}, and it’s one of the biggest times of year for gift vouchers. You don’t sell them online yet. Set one up and I’ll plan a campaign that sells it, with the price on every post.`,
    audience: null,
    items: [],
    action: { label: 'Set up a voucher', path: '/voucher-v2', note: 'Catalogue › Vouchers. Turn on online sales and it shows on your booking page.' },
    score: 78,
  }
}

// ─── Pathways: Stories that differ ───
// Feedback (Ben, 2026-10-06): a week of plans read as "5 very similar
// stories", every one a free-times Story. Plans that run several Stories now
// keep free times to the days that need them most and give the rest a
// different angle each, from the salon's own material only: the moment
// coming up, a review word for word, a face not introduced yet, their own
// recent work. Each angle once; none when there's nothing real to show, and
// then free times it is, as before.
export const MAX_SLOT_STORIES = 2
export const storyAngles = (ctx) => {
  const { now, trade, country, reviews = [], team = [], posts = [], done = () => false, salonName } = ctx
  const out = []
  const m = upcomingMoments(trade, now, country).find(x => !done(`moment:${x.key}`))
  if (m) {
    out.push({ angle: 'moment', type: momentKind(m, ctx), title: `${m.name} Story`, make: (extra) => momentPost(m, ctx, extra) })
  }
  const r = (reviews || []).find(x => x && x.rating >= 4 && x.text && x.text.length >= 20 && x.text.length <= 320 && !done(`review:${x.id}`))
  if (r) {
    const name = first(r.author || r.firstName) || 'A client'
    out.push({ angle: 'review', type: 'review', title: `${name}’s review Story`, make: (extra) => postFor('review', { ...ctx, ...extra, format: 'story', source: `review:${r.id}`, content: { quote: r.text, name, service: r.service || '', rating: r.rating } }) })
  }
  const introduced = new Set((posts || []).filter(p => p && p.type === 'team').map(p => first(p.content && p.content.name).toLowerCase()))
  const t = (team || []).find(x => x && x.name && !introduced.has(first(x.name).toLowerCase()) && !done(`team:${x.id}`))
  if (t) {
    const name = first(t.name)
    out.push({ angle: 'team', type: 'team', title: `Meet ${name} Story`, make: (extra) => postFor('team', { ...ctx, ...extra, format: 'story', source: `team:${t.id}`, content: { name, ...(t.role ? { role: t.role } : {}) }, photos: t.image ? { main: { src: t.image, fx: 0.5, fy: 0.35, zoom: 1 } } : undefined }) })
  }
  // A photo the date's Story already wears isn't shown twice
  const momentPhoto = m && m.type === 'freshWork' && (ctx.photos || [])[0]
  const photo = (ctx.photos || [])[momentPhoto ? 1 : 0]
  if (photo) {
    out.push({ angle: 'work', type: 'freshWork', title: 'Recent work Story', make: (extra) => postFor('freshWork', { ...ctx, ...extra, format: 'story', source: null, content: { detail: 'Fresh from the chair', headline: `New at *${salonName || 'the salon'}*.` }, photos: { main: { src: photo, fx: 0.5, fy: 0.4, zoom: 1 } } }) })
  }
  return out
}

// Which of n Story slots stay free times: the first, then the emptiest, up to
// MAX_SLOT_STORIES; every other slot takes the next angle while there is one.
// `emptiestIdx` is the index of the day with the most room.
export const storyPlan = (n, angles, emptiestIdx = 0) => {
  const slotIdx = new Set([0, emptiestIdx].slice(0, MAX_SLOT_STORIES))
  let a = 0
  return Array.from({ length: n }, (_, i) => {
    if (slotIdx.has(i) || a >= angles.length) return { slots: true }
    const angle = angles[a]
    a += 1
    return { slots: false, angle }
  })
}

// Next week has a lot of room: a week-long campaign to fill it, starting a
// few days before. An email with the best times, a Story each quiet morning,
// a post midweek and a text the evening before the emptiest day. Numbers are
// the diary's own.
const quietWeekPlan = (ctx) => {
  const { now, week, salonName, segments } = ctx
  if (!Array.isArray(week) || week.length < 10) return null
  const nextMon = moment(now).startOf('isoWeek').add(1, 'week')
  const inWeek = week.filter(w => moment(w.date).isBetween(nextMon, moment(nextMon).endOf('isoWeek'), 'day', '[]'))
  const quiet = inWeek.filter(w => w.freeHours >= 4)
  if (quiet.length < 3) return null
  const hours = Math.round(inWeek.reduce((a, w) => a + (w.freeHours || 0), 0))
  const emptiest = quiet.slice().sort((a, b) => b.freeHours - a.freeHours)[0]
  const pick = designPicker({ family: ctx.family })
  const items = []
  let emailAt = at(moment(nextMon).subtract(3, 'days'), 10)
  if (!emailAt.isAfter(now)) emailAt = at(moment(now).add(1, 'day'), 10)
  items.push({
    id: 'email', channel: 'email', at: emailAt.toISOString(), on: true, title: 'Next week’s free times email',
    email: {
      templateId: templateFor('fill', ctx.trade),
      subject: 'Times free next week at {{SalonName}}',
      preheader: 'Book the one that suits you.',
      hero: { eyebrow: 'Next week', headline: 'Room for *you* next week', body: `${ctx.self ? 'I’ve' : 'We’ve'} times free next week, including ${moment(emptiest.date).format('dddd')}. Book the one that suits you online.`, button: 'See the times', buttonUrl: '{{BookingLink}}' },
    },
  })
  // A Story on each of the four quietest days at most, in date order: free
  // times on the first and the emptiest, a different angle on the rest
  const storyDays = quiet.slice().sort((a, b) => b.freeHours - a.freeHours).slice(0, 4).sort((a, b) => (a.date < b.date ? -1 : 1))
  const mix = storyPlan(storyDays.length, storyAngles(ctx), Math.max(0, storyDays.findIndex(w => w.date === emptiest.date)))
  storyDays.forEach((w, n) => {
    const whose = w.who ? { staffId: w.who.id, staffName: w.who.firstName } : {}
    const type = w.count === 1 ? 'oneSlot' : 'freeSlots'
    const slot = mix[n]
    if (!slot.slots) {
      const post = slot.angle.make({ design: pick(slot.angle.type) })
      items.push({ id: `story${n + 1}`, channel: 'story', at: at(w.date, 8).toISOString(), on: true, title: slot.angle.title, angle: slot.angle.angle, post })
      return
    }
    items.push({ id: `story${n + 1}`, channel: 'story', at: at(w.date, 8).toISOString(), on: true, title: `${moment(w.date).format('dddd')}’s free times`, angle: 'slots', post: postFor(type, { ...ctx, design: pick(type), format: 'story', source: `slots:${w.date}`, content: w.count === 1 ? { date: w.date, time: (w.times || [])[0], ...whose } : { date: w.date, times: (w.times || []).slice(0, 3), more: w.count > 3, ...whose } }) })
  })
  const mid = quiet[Math.floor(quiet.length / 2)]
  items.push({ id: 'post', channel: 'post', at: at(moment(mid.date).subtract(1, 'day'), 12, 30).toISOString(), on: true, title: 'Free times post', post: postFor('freeSlots', { ...ctx, design: pick('freeSlots'), format: 'post', source: `slots:${mid.date}:post`, content: { date: mid.date, times: (mid.times || []).slice(0, 3), more: mid.count > 3 } }) })
  const text = smsText([`Hi {{FirstName}}, ${ctx.self ? 'I’ve' : 'we’ve'} spaces at {{SalonName}} on ${moment(emptiest.date).format('dddd')}. Book yours: {{BookingLink}}`], salonName)
  items.push({ id: 'sms', channel: 'sms', at: at(moment(emptiest.date).subtract(1, 'day'), 18).toISOString(), on: true, title: `Spaces on ${moment(emptiest.date).format('dddd')}`, message: text.message })
  const kept = items.filter(i => moment(i.at).isAfter(now) && !(ctx.noClients && (i.channel === 'email' || i.channel === 'sms')))
  const stories = items.some(i => i.channel === 'story' && i.angle && i.angle !== 'slots') ? 'Free times on the emptiest days, a different Story on the others' : 'A week of free-times Stories'
  return {
    key: `quietweek:${iso(nextMon)}`,
    kind: 'quiet',
    title: 'Fill next week',
    why: `Next week has about ${hours} free hours in the diary, with ${quiet.length} days at four hours or more. ${ctx.noClients ? `${stories} and a post put them in front of your followers.` : `${stories}, an email and one text usually fill a good few.`}`,
    audience: fillAudience(segments, Math.min(12, quiet.reduce((a, w) => a + (w.count || 0), 0))),
    items: kept,
    score: 70 + Math.min(20, quiet.length * 3),
  }
}

// The same day empty week after week: a four-week campaign for that day. A
// post the day before and a Story on the morning, each week, and one text.
const quietDayPlan = (ctx) => {
  const { now, week, salonName, segments } = ctx
  if (!Array.isArray(week) || week.length < 21) return null
  const byDow = {}
  week.forEach((w) => { const d = moment(w.date).isoWeekday(); (byDow[d] = byDow[d] || []).push(w) })
  const avg = (list) => list.reduce((a, w) => a + (w.freeHours || 0), 0) / Math.max(1, list.length)
  const overall = avg(week)
  const pick = Object.keys(byDow).map(Number)
    .map(d => ({ d, list: byDow[d], mean: avg(byDow[d]) }))
    .filter(x => x.list.filter(w => w.freeHours >= 4).length >= 3 && x.mean >= overall * 1.4)
    .sort((a, b) => b.mean - a.mean)[0]
  if (!pick) return null
  const name = moment().isoWeekday(pick.d).format('dddd')
  const dates = pick.list.map(w => w.date).filter(d => moment(d).isAfter(moment(now).add(1, 'day'), 'day')).slice(0, 4)
  if (dates.length < 2) return null
  const items = []
  const design = designPicker({ family: ctx.family })
  // The post each week carries that day's times; the Stories after the first
  // week take a different angle each while there is one
  const mix = storyPlan(dates.length, storyAngles(ctx), 0)
  dates.forEach((date, n) => {
    const w = pick.list.find(x => x.date === date)
    items.push({ id: `post${n + 1}`, channel: 'post', at: at(moment(date).subtract(1, 'day'), 12, 30).toISOString(), on: true, title: `${name} post, week ${n + 1}`, post: postFor('freeSlots', { ...ctx, design: design('freeSlots'), format: 'post', source: `slots:${date}:post`, content: { date, times: (w.times || []).slice(0, 3), more: w.count > 3 } }) })
    const slot = mix[n]
    if (!slot.slots) {
      items.push({ id: `story${n + 1}`, channel: 'story', at: at(date, 8).toISOString(), on: true, title: `${slot.angle.title}, week ${n + 1}`, angle: slot.angle.angle, post: slot.angle.make({ design: design(slot.angle.type) }) })
      return
    }
    items.push({ id: `story${n + 1}`, channel: 'story', at: at(date, 8).toISOString(), on: true, title: `${name} Story, week ${n + 1}`, angle: 'slots', post: postFor('freeSlots', { ...ctx, design: design('freeSlots'), format: 'story', source: `slots:${date}`, content: { date, times: (w.times || []).slice(0, 3), more: w.count > 3 } }) })
  })
  const text = smsText([`Hi {{FirstName}}, ${name}s are the easiest day to get in at {{SalonName}}. Book yours: {{BookingLink}}`], salonName)
  items.push({ id: 'sms', channel: 'sms', at: at(moment(dates[0]).subtract(1, 'day'), 18).toISOString(), on: true, title: `${name}s text`, message: text.message })
  return {
    key: `quietday:${pick.d}:${iso(dates[0])}`,
    kind: 'quiet',
    title: `Fill your ${name}s`,
    why: `${name}s are your quietest day for the next few weeks, with about ${Math.round(pick.mean)} free hours each. A post and a Story every ${name} for a month${ctx.noClients ? '.' : ', and one text to start it.'}`,
    audience: fillAudience(segments, 6),
    items: items.filter(i => moment(i.at).isAfter(now) && !(ctx.noClients && i.channel === 'sms')),
    score: 55,
  }
}

const reviewPlan = (ctx) => {
  const { now, reviews, done } = ctx
  const r = (reviews || []).find(x => x && x.rating >= 4 && x.text && x.text.length >= 20 && x.text.length <= 320 && !done(`review:${x.id}`))
  if (!r) return null
  let day = moment(now).add(1, 'day')
  if (day.day() === 0) day = day.add(1, 'day')
  const content = { quote: r.text, name: first(r.author || r.firstName) || 'A client', service: r.service || '', rating: r.rating }
  return {
    key: `review:${r.id}`,
    kind: 'review',
    title: `Share ${content.name}’s review`,
    why: `${content.name} left you ${r.rating} stars. Shared word for word, a review does more for people deciding where to book than anything you could say yourself.`,
    audience: null,
    items: [
      { id: 'post', channel: 'post', at: at(day, 12, 30).toISOString(), on: true, title: 'Review post', post: postFor('review', { ...ctx, format: 'post', source: `review:${r.id}`, content }) },
      { id: 'story', channel: 'story', at: at(day, 18).toISOString(), on: true, title: 'Review Story', post: postFor('review', { ...ctx, format: 'story', source: `review:${r.id}`, content }) },
    ],
    score: 60,
  }
}

const introPlan = (ctx) => {
  const { now, team, posts, done, trade, segments } = ctx
  const introduced = new Set((posts || []).filter(p => p.type === 'team').map(p => first(p.content && p.content.name).toLowerCase()))
  const m = (team || []).find(t => t && t.name && !introduced.has(first(t.name).toLowerCase()) && !done(`team:${t.id}`))
  if (!m) return null
  let day = moment(now).add(1, 'day')
  while ([0, 6].includes(day.day())) day = day.add(1, 'day')
  const name = first(m.name)
  const content = { name, ...(m.role ? { role: m.role } : {}) }
  const photos = m.image ? { main: { src: m.image, fx: 0.5, fy: 0.35, zoom: 1 } } : undefined
  return {
    key: `team:${m.id}`,
    kind: 'intro',
    title: `Introduce ${name}`,
    why: `${name} hasn’t had an introduction yet. People book people, so a face and a name makes it easy to pick them.`,
    audience: pickSegment(segments, ['all'], 'All your clients'),
    items: [
      { id: 'post', channel: 'post', at: at(day, 12, 30).toISOString(), on: true, title: `Meet ${name}`, post: postFor('team', { ...ctx, format: 'post', source: `team:${m.id}`, content, photos }) },
      { id: 'story', channel: 'story', at: at(day, 18).toISOString(), on: true, title: `${name} Story`, post: postFor('team', { ...ctx, format: 'story', source: `team:${m.id}`, content, photos }) },
      !ctx.noClients && {
        id: 'email', channel: 'email', at: at(day, 10).toISOString(), on: false, title: `Meet ${name} email`,
        email: {
          templateId: templateFor('intro', trade),
          subject: `Say hello to ${name}`,
          preheader: `${name} has joined {{SalonName}}. Book in with them online.`,
          hero: { eyebrow: 'Meet the team', headline: `Say hello to *${name}*`, body: `${name}${m.role ? `, our ${String(m.role).toLowerCase()},` : ''} is part of the team at {{SalonName}}. Book in with them whenever suits you.`, button: `Book with ${name}`, buttonUrl: '{{BookingLink}}', ...(m.image ? { image: m.image, imageAlt: name } : {}) },
        },
      },
    ].filter(Boolean),
    score: 40,
  }
}

// Quiet clients are a journey's job (Marketing › Journeys): each client is
// welcomed back when they go quiet, and it stops when they book. Bailey
// suggests switching that on, never a one-off send that would double it.
const winbackPlan = (ctx) => {
  const { segments, done, journeys } = ctx
  const lapsed = (segments || []).find(s => s && s.id === 'inactive' && Number(s.count) >= 10)
  if (!lapsed || done('winback')) return null
  if (journeys === null || journeys === undefined) return null // not known yet: say nothing
  if (journeys.some(j => j && j.status === 'ready' && j.trigger && j.trigger.type === 'lapsed')) return null
  return {
    key: 'winback:journey',
    kind: 'winback',
    title: 'Welcome back quiet clients',
    why: `${lapsed.count} clients haven’t been in for a while. The win-back journey welcomes each one back as they go quiet, and stops the moment they book.`,
    audience: { segment: lapsed.id, label: lapsed.name || 'Clients who haven’t been in for a while', count: Number(lapsed.count) || null },
    items: [],
    action: { label: 'Set up the journey', path: '/marketing-journeys', state: { openRecipe: 'win-back' } },
    score: 30,
  }
}

const RECIPES = [fillPlan, momentPlan, nextMomentPlan, vouchersPlan, quietWeekPlan, quietDayPlan, reviewPlan, introPlan, winbackPlan]

// "Ask Bailey" on a day in the calendar: fill that day's gaps if it has any,
// otherwise something worth posting that day
export const planForDay = ({ day, now = new Date(), trade = 'hair', salonName = '', brandColour = null, family = 'editorial', week = [], segments = [], posts = [] } = {}) => {
  const D = moment(day).startOf('day')
  if (D.isBefore(moment(now).startOf('day'))) return null
  const ctx = { now, trade, salonName, brandColour, family, segments, done: () => false, photos: photosFromPosts(posts) }
  const gaps = (week || []).find(w => w.date === D.format('YYYY-MM-DD') && w.count >= 1)
  if (gaps) {
    // Any gap counts when the salon has picked the day themselves
    const p = fillPlan({ ...ctx, week: [{ ...gaps, freeHours: Math.max(gaps.freeHours, 3) }] })
    if (p) return withWindow(p)
  }
  const photo = (ctx.photos || [])[0]
  const postAt = at(D, 12, 30).isAfter(now) ? at(D, 12, 30) : moment(now).add(30, 'minutes').startOf('minute')
  const storyAt = at(D, 18).isAfter(now) ? at(D, 18) : moment(postAt).add(2, 'hours')
  return withWindow({
    key: `day:${D.format('YYYY-MM-DD')}`,
    kind: 'day',
    title: `Something for ${said(D, now) === 'today' ? 'today' : said(D, now) === 'tomorrow' ? 'tomorrow' : D.format('dddd')}`,
    why: photo
      ? 'Your diary has no gaps that day, so I suggest showing off your work instead: a post at lunchtime and a Story in the evening, when people are scrolling.'
      : 'Your diary has no gaps that day, so I suggest a helpful tip instead: a post at lunchtime and a Story in the evening, when people are scrolling. Add photos of your work and I can show those off too.',
    audience: null,
    items: photo ? [
      { id: 'post', channel: 'post', at: postAt.toISOString(), on: true, title: 'Fresh work post', post: postFor('freshWork', { ...ctx, format: 'post', source: null, content: {}, photos: { main: { src: photo, fx: 0.5, fy: 0.4, zoom: 1 } } }) },
      { id: 'story', channel: 'story', at: storyAt.toISOString(), on: true, title: 'Fresh work Story', post: postFor('freshWork', { ...ctx, format: 'story', source: null, content: {}, photos: { main: { src: photo, fx: 0.5, fy: 0.4, zoom: 1 } } }) },
    ] : [
      { id: 'post', channel: 'post', at: postAt.toISOString(), on: true, title: 'Tip post', post: postFor('tips', { ...ctx, format: 'post', source: null, content: {} }) },
      { id: 'story', channel: 'story', at: storyAt.toISOString(), on: true, title: 'Tip Story', post: postFor('tips', { ...ctx, format: 'story', source: null, content: {} }) },
    ],
    score: 0,
  })
}

// Every season in the next year as a campaign to start from, whether or not
// Bailey would suggest it yet (the builder's "Start from" list)
export const seasonTemplates = ({ now = new Date(), trade = 'hair', country = 'GB', posts = [], ...rest } = {}) => {
  const ctx = { now, trade, country, salonName: '', brandColour: null, family: 'editorial', segments: [], week: [], photos: photosFromPosts(posts), ...rest, done: () => false }
  return momentTemplates(trade, now, country).map(m => withWindow(seasonPlan(m, ctx))).filter(Boolean)
}

// Bailey's plans as suggestions on the calendar (dashed, "Suggested")
export const planSuggestions = (plans = []) => plans.flatMap(p => p.items.filter(i => i.on).map(i => ({
  id: `${p.key}:${i.id}`,
  planKey: p.key,
  at: new Date(i.at),
  title: `${p.title}: ${CHANNEL_NAMES[i.channel].toLowerCase()}`,
})))

// ─── Pathways: the goal first ───
// A salon's active Bailey goals (agent/goals: { intent, title }) lift the
// plans that move them, and the plan says so. No goal: the order is as before
// (dates and the diary decide). Pure, so the boost is easy to test.
const GOAL_FITS = {
  'fill-quiet-days': [{ kinds: ['fill', 'quiet'], lift: 40 }],
  'more-bookings': [{ kinds: ['fill', 'quiet'], lift: 25 }, { kinds: ['moment'], lift: 15 }],
  'win-back-lapsed': [{ kinds: ['winback'], lift: 100 }],
  'more-per-visit': [{ kinds: ['moment'], occasion: 'gift', lift: 30 }],
  'more-revenue': [{ kinds: ['moment'], occasion: 'gift', lift: 30 }, { kinds: ['fill', 'quiet'], lift: 15 }],
}
export const goalFor = (plan, goals = []) => {
  let best = null
  ;(goals || []).forEach((g) => {
    ;(GOAL_FITS[g && g.intent] || []).forEach((fit) => {
      if (!fit.kinds.includes(plan.kind)) return
      if (fit.occasion && plan.occasion !== fit.occasion) return
      if (!best || fit.lift > best.lift) best = { goal: g, lift: fit.lift }
    })
  })
  return best
}
const withGoal = (plan, goals) => {
  const fit = goalFor(plan, goals)
  if (!fit) return plan
  const title = String(fit.goal.title || '').trim()
  return { ...plan, goal: { id: fit.goal.id || fit.goal._id || null, title, intent: fit.goal.intent }, why: title ? `For your goal, “${title}”. ${plan.why}` : plan.why, score: plan.score + fit.lift }
}

// memory: { dismissed: { key: { at, reason } }, approved: { key: { at } },
//          progress: { key: { at, done } } } (progress: part-approved, so
//          offered to finish; marked `partial`)
// The context every recipe reads, from what the salon has (also used by
// Bailey's Playbook, data/playbook.js, so its plans see exactly the same)
export const planContext = ({
  now = new Date(), trade = 'hair', salonName = '', brandColour = null, family = 'editorial',
  week = null, reviews = [], team = [], segments = [], posts = [], memory = {},
  country = 'GB', journeys, campaigns = [], vouchers = null, tone = null, links = {}, currency = '£',
  self = null, me = null, specialism = null,
} = {}) => {
  const dismissed = memory.dismissed || {}
  const approved = memory.approved || {}
  const sources = new Set((posts || []).map(p => p && p.source).filter(Boolean))
  const kindOf = (key) => String(key).split(':')[0]
  const recently = (entry, days) => entry && moment(now).diff(moment(entry.at), 'days') < days
  // A key the salon has already turned into posts, approved, or said no to;
  // "not for us" puts the whole kind of plan away for a month
  // Campaigns the salon has made or approved count as done too
  const made = new Set((campaigns || []).map(c => c && c.planKey).filter(Boolean))
  const done = (key) => sources.has(key) || made.has(key) || !!approved[key] || recently(dismissed[key], 14)
    || Object.keys(dismissed).some(k => kindOf(k) === kindOf(key) && dismissed[k].reason === 'not-for-us' && recently(dismissed[k], 30))
  // A self-employed stylist (`self`: { id }) markets their own book: their
  // own reviews only (a review that names them), no colleagues to introduce,
  // and the salon's vouchers aren't theirs to sell. Texts say "I".
  const own = self && self.id ? String(self.id) : null
  const scoped = self ? {
    reviews: (reviews || []).filter(r => r && own && r.staffId && String(r.staffId) === own),
    team: [],
    vouchers: [],
  } : {}
  // Nobody to email or text yet: every audience the salon has is empty
  const list = Array.isArray(segments) ? segments : []
  const all = list.find(s => s && s.id === 'all')
  const noClients = all ? Number(all.count) === 0 : (list.length > 0 && list.every(s => s && Number(s.count) === 0))
  // Fully booked for the next fortnight (the diary is known and has under
  // four hours free): booking campaigns can wait
  const full = Array.isArray(week) && week.filter(w => moment(w.date).diff(moment(now).startOf('day'), 'days') < 14).reduce((a, w) => a + (Number(w.freeHours) || 0), 0) < 4
  return { now, trade, specialism, salonName, brandColour, family, week, reviews, team, segments, posts, done, country, journeys, vouchers, tone, links, currency, photos: photosFromPosts(posts), self, me, noClients, full, ...scoped }
}

export const buildPlans = ({
  now = new Date(), trade = 'hair', salonName = '', brandColour = null, family = 'editorial',
  week = null, reviews = [], team = [], segments = [], posts = [], memory = {}, max = 3,
  country = 'GB', journeys, campaigns = [], vouchers = null, tone = null, links = {}, currency = '£', goals = [],
  self = null, specialism = null,
} = {}) => {
  const dismissed = memory.dismissed || {}
  const progress = memory.progress || {}
  const recently = (entry, days) => entry && moment(now).diff(moment(entry.at), 'days') < days
  const ctx = planContext({ now, trade, specialism, salonName, brandColour, family, week, reviews, team, segments, posts, memory, country, journeys, campaigns, vouchers, tone, links, currency, self })
  const { done, noClients } = ctx
  // "Too salesy" last time: texts start switched off
  const salesy = Object.values(dismissed).some(d => d.reason === 'salesy' && recently(d, 30))
  const finish = (plan) => {
    if (!plan || !(plan.items.length || plan.action) || done(plan.key)) return null
    let p = plan
    // Posts and Stories only, when there's nobody to email or text: no
    // "All your clients (0)" under them
    if (noClients && p.audience && !p.items.some(i => i.channel === 'email' || i.channel === 'sms')) p = { ...p, audience: null }
    if (salesy) p = { ...p, items: p.items.map(it => (it.channel === 'sms' ? { ...it, on: false } : it)) }
    // Their goal first (pathways)
    p = withGoal(p, goals)
    // Part-approved: offered first, to finish what was started
    if (progress[p.key]) p = { ...p, partial: true, score: p.score + 1000 }
    return p
  }
  const run = (fn, c) => { try { return finish(withWindow(fn(c))) } catch (e) { return null } }
  const sorted = RECIPES.map(fn => ({ fn, plan: run(fn, ctx) })).filter(x => x.plan).sort((a, b) => b.plan.score - a.plan.score)
  // One review and one face in one plan. A plan below one that already shows
  // a review or a person is made again without them: the next review,
  // someone else, or not offered at all.
  const quotes = new Set()
  const names = new Set()
  const repeats = p => p.items.some(i => i.post && ((i.post.type === 'review' && quotes.has(i.post.content.quote)) || (i.post.type === 'team' && names.has(i.post.content.name))))
  const out = []
  sorted.forEach(({ fn, plan }) => {
    if (out.length >= max) return
    let p = plan
    if (repeats(p)) {
      p = run(fn, { ...ctx, reviews: (ctx.reviews || []).filter(r => r && !quotes.has(r.text)), team: (ctx.team || []).filter(t => t && !names.has(first(t.name))) })
      if (!p || repeats(p)) return
    }
    p.items.forEach((i) => {
      if (i.post && i.post.type === 'review' && i.post.content.quote) quotes.add(i.post.content.quote)
      if (i.post && i.post.type === 'team' && i.post.content.name) names.add(i.post.content.name)
    })
    out.push(p)
  })
  return out
}

export const CHANNEL_NAMES = { sms: 'Text', email: 'Email', post: 'Post', story: 'Story' }
export { windowOf }
export const DISMISS_REASONS = [
  { id: 'later', label: 'Not this week' },
  { id: 'salesy', label: 'Too salesy' },
  { id: 'wrong-day', label: 'Wrong day' },
  { id: 'not-for-us', label: 'Not for us' },
]

// ─── For Bailey's Playbook (data/playbook.js) ───
// The server picks the scenario; these build what its option asks for, with
// the same recipes and helpers as the plans above.
export const RECIPE_FNS = { fill: fillPlan, quiet: quietWeekPlan, quietWeekday: quietDayPlan, review: reviewPlan, intro: introPlan, winbackJourney: winbackPlan, vouchers: vouchersPlan }
export { postFor, at, said, Said, pickSegment, voucherFrom, withWindow, first }
